
import React, { useState, useEffect } from 'react';
import { useOrders } from './hooks/useOrders';

interface OrderFulfillmentProps {
  orderId?: string;
}

export const OrderFulfillment: React.FC<OrderFulfillmentProps> = ({ orderId }) => {
  const { orders, updateOrderStatus, downloadFinishedProduct, transferToBranch, notifyCustomer } = useOrders();
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showQCModal, setShowQCModal] = useState(false);
  const [qcNotes, setQCNotes] = useState('');
  const [transferDetails, setTransferDetails] = useState({
    branchId: '',
    trackingNumber: '',
    courierService: ''
  });

  useEffect(() => {
    if (orderId) {
      const order = orders.find(o => o.id === orderId);
      setSelectedOrder(order);
    }
  }, [orderId, orders]);

  const handleDownloadProduct = async (order: any) => {
    try {
      await downloadFinishedProduct(order.id);
      await updateOrderStatus(order.id, 'received_ho');
      alert('Product downloaded and status updated to received at HO');
    } catch (error) {
      alert('Error downloading product');
    }
  };

  const handleQCApproval = async (order: any, approved: boolean) => {
    try {
      const newStatus = approved ? 'qc_approved' : 'qc_rejected';
      await updateOrderStatus(order.id, newStatus, qcNotes);
      setShowQCModal(false);
      setQCNotes('');
      alert(`Product ${approved ? 'approved' : 'rejected'} in QC`);
    } catch (error) {
      alert('Error updating QC status');
    }
  };

  const handleTransferToBranch = async (order: any) => {
    try {
      await transferToBranch(order.id, transferDetails);
      await updateOrderStatus(order.id, 'transferred_to_branch');
      await notifyCustomer(order.customer_id, 'product_shipped', {
        orderNumber: order.order_number,
        trackingNumber: transferDetails.trackingNumber
      });
      alert('Product transferred to branch and customer notified');
    } catch (error) {
      alert('Error transferring to branch');
    }
  };

  const handleDeliveryToCustomer = async (order: any) => {
    try {
      await updateOrderStatus(order.id, 'delivered');
      await notifyCustomer(order.customer_id, 'product_delivered', {
        orderNumber: order.order_number
      });
      alert('Product delivered to customer');
    } catch (error) {
      alert('Error updating delivery status');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'confirmed': 'bg-blue-100 text-blue-800',
      'under_manufacture': 'bg-yellow-100 text-yellow-800',
      'manufactured': 'bg-purple-100 text-purple-800',
      'received_ho': 'bg-indigo-100 text-indigo-800',
      'qc_approved': 'bg-green-100 text-green-800',
      'qc_rejected': 'bg-red-100 text-red-800',
      'hallmarked': 'bg-teal-100 text-teal-800',
      'tagged': 'bg-cyan-100 text-cyan-800',
      'transferred_to_branch': 'bg-orange-100 text-orange-800',
      'received_branch': 'bg-lime-100 text-lime-800',
      'delivered': 'bg-emerald-100 text-emerald-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getNextAction = (status: string) => {
    switch (status) {
      case 'manufactured':
        return 'Download from Vendor';
      case 'received_ho':
        return 'Quality Check';
      case 'qc_approved':
        return 'Send for Hallmarking';
      case 'hallmarked':
        return 'Generate Tag';
      case 'tagged':
        return 'Transfer to Branch';
      case 'transferred_to_branch':
        return 'Confirm Receipt at Branch';
      case 'received_branch':
        return 'Deliver to Customer';
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Order Fulfillment</h1>
        <div className="flex space-x-2">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap">
            <i className="ri-refresh-line mr-2"></i>
            Refresh Orders
          </button>
        </div>
      </div>

      {/* Order Status Pipeline */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Fulfillment Pipeline</h2>
        <div className="flex items-center justify-between">
          {[
            'Confirmed',
            'Manufacturing',
            'Manufactured',
            'Received HO',
            'QC Approved',
            'Hallmarked',
            'Tagged',
            'Transferred',
            'Delivered'
          ].map((step, index) => (
            <div key={step} className="flex items-center">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <span className="text-xs mt-1 text-center">{step}</span>
              </div>
              {index < 8 && (
                <div className="w-12 h-0.5 bg-gray-200 mx-2"></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Orders in Fulfillment</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expected Delivery
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.filter(order => ['confirmed', 'under_manufacture', 'manufactured', 'received_ho', 'qc_approved', 'hallmarked', 'tagged', 'transferred_to_branch', 'received_branch'].includes(order.status)).map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">#{order.order_number}</div>
                      <div className="text-sm text-gray-500">{order.product_name}</div>
                      <div className="text-sm text-gray-500">₹{order.total_amount?.toLocaleString()}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{order.customer_name}</div>
                    <div className="text-sm text-gray-500">{order.customer_phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                      {order.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {order.expected_delivery_date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {order.status === 'manufactured' && (
                        <button
                          onClick={() => handleDownloadProduct(order)}
                          className="text-blue-600 hover:text-blue-900 whitespace-nowrap"
                        >
                          <i className="ri-download-line mr-1"></i>
                          Download
                        </button>
                      )}
                      {order.status === 'received_ho' && (
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowQCModal(true);
                          }}
                          className="text-green-600 hover:text-green-900 whitespace-nowrap"
                        >
                          <i className="ri-check-line mr-1"></i>
                          QC Check
                        </button>
                      )}
                      {order.status === 'qc_approved' && (
                        <button
                          onClick={() => updateOrderStatus(order.id, 'hallmarked')}
                          className="text-purple-600 hover:text-purple-900 whitespace-nowrap"
                        >
                          <i className="ri-award-line mr-1"></i>
                          Hallmark
                        </button>
                      )}
                      {order.status === 'hallmarked' && (
                        <button
                          onClick={() => updateOrderStatus(order.id, 'tagged')}
                          className="text-indigo-600 hover:text-indigo-900 whitespace-nowrap"
                        >
                          <i className="ri-price-tag-line mr-1"></i>
                          Generate Tag
                        </button>
                      )}
                      {order.status === 'tagged' && (
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            // Show transfer modal
                          }}
                          className="text-orange-600 hover:text-orange-900 whitespace-nowrap"
                        >
                          <i className="ri-truck-line mr-1"></i>
                          Transfer
                        </button>
                      )}
                      {order.status === 'received_branch' && (
                        <button
                          onClick={() => handleDeliveryToCustomer(order)}
                          className="text-emerald-600 hover:text-emerald-900 whitespace-nowrap"
                        >
                          <i className="ri-gift-line mr-1"></i>
                          Deliver
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* QC Modal */}
      {showQCModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Quality Check - Order #{selectedOrder.order_number}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  QC Notes
                </label>
                <textarea
                  value={qcNotes}
                  onChange={(e) => setQCNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  placeholder="Enter quality check observations..."
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleQCApproval(selectedOrder, false)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 whitespace-nowrap"
                >
                  <i className="ri-close-line mr-2"></i>
                  Reject
                </button>
                <button
                  onClick={() => handleQCApproval(selectedOrder, true)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 whitespace-nowrap"
                >
                  <i className="ri-check-line mr-2"></i>
                  Approve
                </button>
              </div>
              <button
                onClick={() => setShowQCModal(false)}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderFulfillment;
